import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLock, FiLogOut, FiPlus, FiAlertCircle, FiX, FiCheck, FiGrid, FiBarChart2, FiSettings } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ClientForm from '../components/admin/ClientForm';
import ClientList from '../components/admin/ClientList';
import DashboardOverview from '../components/admin/DashboardOverview';
import RevenueReport from '../components/admin/RevenueReport';
import AdminSettings from '../components/admin/AdminSettings';
import {
  createClientRecord, getBillingSession, listClients, advanceDueDate,
  signInAdmin, signOutAdmin, updateClientRecord, logPayment,
  archiveClient, restoreClient, deleteClient, listCountries
} from '../services/billing';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: FiGrid },
  { id: 'reports', label: 'Reports', icon: FiBarChart2 },
  { id: 'settings', label: 'Settings', icon: FiSettings }
];

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  const refresh = useCallback(async () => {
    try {
      setClients(await listClients());
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      setCountries(await listCountries());
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  }, []);

  useEffect(() => {
    let active = true;
    getBillingSession()
      .then(currentUser => {
        if (active) setUser(currentUser?.email?.toLowerCase() === 'admin@hostfig.com' ? currentUser : null);
      })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (user) {
      refresh();
      loadCountries();
    }
  }, [user, refresh, loadCountries]);

  const login = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      setUser(await signInAdmin(password));
      setPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const save = async (record) => {
    setError('');
    setSaving(true);
    try {
      if (editClient) {
        await updateClientRecord(editClient.id, record);
        setEditClient(null);
      } else {
        await createClientRecord(record);
      }
      await refresh();
      setShowForm(false);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const change = async (client, updates) => {
    setError('');
    setBusyId(client.id);
    try {
      await updateClientRecord(client.id, updates);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (client) => {
    setError('');
    setBusyId(client.id);
    try {
      await logPayment(client);
      await updateClientRecord(client.id, {
        next_due_date: advanceDueDate(client.next_due_date, client.billing_frequency)
      });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleEdit = (client) => {
    setEditClient(client);
    setShowForm(true);
  };

  const handleArchive = async (client) => {
    setError('');
    setBusyId(client.id);
    try {
      await archiveClient(client.id);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (client) => {
    setError('');
    setBusyId(client.id);
    try {
      await restoreClient(client.id);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (client) => {
    setError('');
    setBusyId(client.id);
    try {
      await deleteClient(client.id);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditClient(null);
  };

  const logout = async () => {
    try {
      await signOutAdmin();
      setUser(null);
      setClients([]);
    } catch (err) {
      setError(err.message);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Verifying access...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 md:px-8">
          <div className="flex items-center gap-4">
            <img
              src="https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783586850/No_Backgournd_just_logo_pl2cm4.png"
              alt="HostFig"
              className="h-10 w-auto object-contain"
            />
            <div className="hidden sm:flex items-center gap-2 pl-4 border-l border-slate-200">
              <span className="text-sm font-semibold text-slate-900">Admin</span>
              <span className="text-xs text-slate-400">/</span>
              <span className="text-sm text-slate-500">Billing</span>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-3">
              {activeTab === 'dashboard' && (
                <button
                  onClick={() => { setEditClient(null); setShowForm(true); }}
                  className="hidden sm:flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 shadow-sm shadow-blue-600/20"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  Add Client
                </button>
              )}
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:border-slate-300"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8 md:px-8 md:py-12">
        {/* Page Title */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">HostFig Workspace</p>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
              {user ? 'Billing Dashboard' : 'Welcome back'}
            </h1>
            <p className="mt-2 text-slate-500">
              {user
                ? 'Manage client subscriptions, track payments, and send reminders.'
                : 'Sign in to access the admin panel.'}
            </p>
          </motion.div>
        </div>

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4"
            >
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700 flex-1">{error}</p>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Login Form */}
        {!user ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center pt-12"
          >
            <div className="w-full max-w-md">
              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/50 md:p-10">
                <div className="mb-8 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/30">
                    <SafeIcon icon={FiLock} className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Admin Sign In</h2>
                  <p className="mt-2 text-sm text-slate-500">Authorized access only</p>
                </div>
                <form onSubmit={login} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                    <div className="relative">
                      <input
                        value="admin@hostfig.com"
                        readOnly
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-slate-500 pr-10"
                      />
                      <SafeIcon icon={FiCheck} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
                    <input
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      placeholder="Enter your password"
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 font-semibold text-white transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/25"
                  >
                    {saving ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Signing in...
                      </span>
                    ) : 'Sign in securely'}
                  </button>
                </form>
              </div>
              <p className="mt-6 text-center text-xs text-slate-400">
                Protected by Supabase authentication
              </p>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="mb-8 flex gap-1 rounded-xl bg-slate-100 p-1 w-fit">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <SafeIcon icon={Icon} className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {activeTab === 'dashboard' && (
                <motion.div
                  key="dashboard"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <DashboardOverview clients={clients} />
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => { setEditClient(null); setShowForm(true); }}
                      className="sm:hidden flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 shadow-sm"
                    >
                      <SafeIcon icon={FiPlus} className="w-4 h-4" />
                      Add New Client
                    </button>
                  </div>
                  <ClientList
                    clients={clients}
                    busyId={busyId}
                    onPaid={markPaid}
                    onToggle={client => change(client, { is_active: !client.is_active })}
                    onEdit={handleEdit}
                    onArchive={handleArchive}
                    onRestore={handleRestore}
                    onDelete={handleDelete}
                  />
                </motion.div>
              )}
              {activeTab === 'reports' && (
                <motion.div
                  key="reports"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <RevenueReport countries={countries} />
                </motion.div>
              )}
              {activeTab === 'settings' && (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <AdminSettings />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Add/Edit Client Modal */}
      <AnimatePresence>
        {showForm && user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={closeForm}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <ClientForm
                onSave={save}
                saving={saving}
                onClose={closeForm}
                editClient={editClient}
                countries={countries}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}